import { Input } from "@/components/ui/input";
import { Label } from "@radix-ui/react-label";
import { cn } from "@/lib/utils";
import { ComponentProps, useId } from "react";
import { FilterColumn, DateRange } from "..";
import DatePicker from "../../ui/DatePicker";

type FilterFieldBaseProps = Omit<
    ComponentProps<typeof Input>,
    "onChange" | "value"
>;

interface FilterFieldProps<T> extends FilterFieldBaseProps {
    column: FilterColumn<T>;
    value: DateRange;
    onChange: (value: DateRange) => void;
}

export default function DateFilterField<T>({
    column,
    value,
    onChange,
    placeholder = "Select Date",
    ...rest
}: FilterFieldProps<T>) {
    const minId = useId();
    const maxId = useId();

    const handleMinChange = (date: Date | null) => {
        onChange({ ...value, min: date });
    };

    const handleMaxChange = (date: Date | null) => {
        onChange({ ...value, max: date });
    };

    return (
        <div className={cn("flex flex-col gap-2")}>
            <Label className={cn("text-sm font-medium")}>
                {column.headingText}
            </Label>
            <div className={cn("flex items-center gap-2")}>
                <div className={cn("flex-1")}>
                    <Label
                        htmlFor={minId}
                        className={cn("text-xs text-muted-foreground")}
                    >
                        From
                    </Label>
                    <DatePicker
                        id={minId}
                        placeholder={placeholder}
                        value={value.min ?? null}
                        onChange={handleMinChange}
                        {...rest}
                    />
                </div>
                <span className={cn("pt-5 text-muted-foreground")}>—</span>
                <div className={cn("flex-1")}>
                    <Label
                        htmlFor={maxId}
                        className={cn("text-xs text-muted-foreground")}
                    >
                        To
                    </Label>
                    <DatePicker
                        id={maxId}
                        placeholder={placeholder}
                        value={value.max ?? null}
                        onChange={handleMaxChange}
                        {...rest}
                    />
                </div>
            </div>
        </div>
    );
}
